import { useMemo } from "react";
import { Link } from "react-router";
import CircularProgress from "@mui/material/CircularProgress";
import Divider from "@mui/material/Divider";
import TimeTransformer from "src/business-components/general/TimeTransformer/TimeTransformer";
import RunStatusBar from "src/business-components/RunsStatusBars/components/RunStatusBar/RunStatusBar";
import RunsStatusBars from "src/business-components/RunsStatusBars/RunsStatusBars";
import { AssetNameWithIcon } from "src/components/resources/AssetNameWithIcon/AssetNameWithIcon";
import { PipelineTableTotalRuns } from "src/pages/PipelinesIndexPage/components/PipelinesTable/components/PipelinesTableCells/PipelinesTableCells";
import type { PipelineGridRow } from "src/pages/PipelinesIndexPage/components/PipelinesTable/PipelinesTable.types";
import { transformPipelineApiData } from "src/pages/PipelinesIndexPage/PipelinesPage.utils";
import { useQueryPipelines } from "src/queries/pipelines/pipeline.query";
import type { PipelineV2 } from "src/queries/pipelines/pipeline.types";
import { useSearchStorage } from "src/storage/useSearchStorage";
import PipelineTags from "../ManagePipelineTagsAndOwners/components/PipelineTags/PipelineTags";
import PipelineSummaryMetrics from "./components/PipelineSummaryMetrics/PipelineSummaryMetrics";
import type { Metrics, PipelineSummaryProps } from "./PipelineSummary.types";

const PipelineSummary = ({
  env_name,
  appName,
  assetType,
  label,
  ...props
}: PipelineSummaryProps) => {
  const { timeWindow } = useSearchStorage();

  const pipelinesQuery = useQueryPipelines<PipelineV2, PipelineGridRow>({
    params: { app_name: appName, env_name },
    select: transformPipelineApiData,
  });

  const data = pipelinesQuery.data as PipelineGridRow | undefined;

  const metrics = useMemo((): Metrics => {
    if (!data) return {};

    const { sla, duration, freshness, volume, distribution, others } = data;

    return {
      sla,
      duration,
      freshness,
      volume,
      distribution,
      others,
    } as unknown as Metrics;
  }, [data]);

  if (pipelinesQuery.isFetching) {
    return <CircularProgress size={24} sx={{ m: "auto", display: "block" }} />;
  }

  if (!data) {
    return null;
  }

  return (
    <div {...props}>
      <AssetNameWithIcon
        type={assetType}
        sx={{ color: "grey.dark" }}
        className="text-grey-600 mb-3"
      />

      <Link relative="path" to={`/envs/${env_name}/apps/${appName}`}>
        {label}
      </Link>

      <div className="text-xs mt-1">
        <PipelineTags tags={data?.tags} max={6} />
      </div>

      <Divider className="my-3!" />

      <section className="mb-3">
        <h3 className="mb-1">Last PIT</h3>

        <Link
          relative="path"
          to={`/envs/${env_name}/apps/${appName}/${data.runs.recentRuns?.at(-1)?.app_pit}?timeWindow=${timeWindow}`}
          className="flex gap-2 items-center"
        >
          <RunStatusBar status={data.runs.recentRuns.at(-1)?.status} />
          <TimeTransformer
            ts={data.runs.recentRuns?.at(-1)?.app_pit}
            withoutTooltip
            ignoreTimezone
          />
        </Link>
      </section>

      <section className="whitespace-nowrap">
        <div className="flex gap-2 justify-between">
          <h3 className="mb-1">Recent runs</h3>
          <div>
            <PipelineTableTotalRuns
              totalRuns={data.runs.totalRuns}
              pipelineName={appName}
            />
          </div>
        </div>
        <RunsStatusBars data={data.runs.recentRuns} pipelineName={appName} />
      </section>

      <Divider className="my-3!" />

      <section>
        <PipelineSummaryMetrics appName={appName} metrics={metrics} />
      </section>
    </div>
  );
};

export default PipelineSummary;
